import React, { useContext, memo } from 'react';
import styled from 'styled-components';

import { fieldHelper } from '@builttocreate/engine-utils';

import FieldTypes from '../../../constants/FieldTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldModes from '../../../constants/FieldModes';
import FileViews from '../../../constants/FileViews';

import FieldLazyLoader from './FieldLazyLoader';
import TextField from './TextField';
import BlockField from './BlockField';
import TextareaField from './TextareaField';
import ImageField from './ImageField';
import FileField from './FileField';
import RTEField from './RTEField';
import DropdownField from './DropdownGenericField';
import DropdownSelectionField from './DropdownSelectionField';
import SignatureField from './SignatureField';
import MultiSelectField from './MultiSelectField';
import MultiSelectSelectionField from './MultiSelectSelectionField';
import ChartField from './chart/ChartField';

import InputGroupField from './inputGroup/InputGroupField';
import TableField from './table/TableField';
import TableTextField from './table/TableTextField';
import TableImageField from './table/TableImageField';
import TableDropdownField from './table/TableDropdownField';
import TableDropdownSelectionField from './table/TableDropdownSelectionField';

import Icon from '../../misc/Icon';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import { getFieldThemeObject } from '../../../theme/getThemeObject';

const { isFieldEmpty } = fieldHelper;

/**
 * <-----------------------NOTICE------------------>
 * Overflow is visible in readOnly mode to ensure that the field value
 * appears fully without being cut off due to inadequate field height
 */
const StyledFieldWrapper = styled.div`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  overflow: ${(props) => (props.disabled ? 'visible' : ' hidden')};
`;

const StyledField = styled.div`
  padding-top: ${(props) => props.overflow || 0}px;
  height: 100%;
`;

const StyledFieldIndicatorWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -6px;
  left: -4px;
`;

const StyledFieldIndicator = styled.div`
  width: 15px;
  height: 15px;
  text-align: center;
  border-radius: 100px;
  border-color: transparent;
  z-index:1;
  margin-right: 2px;
  cursor: default;
  svg {
    font-size: 14px;
    vertical-align: 0;
  }
`;

const Field = (props) => {

  const {
    _id,
    field,
    identifier,
    type,
    view,
    displayType,
    columnType,
    disabled,
    required,
    hidden,
    virtual,
    lazyload,
    value,
    options,
    cellValue,

    fieldMode,
    fieldPresentationMode,
    fieldDefaultStyles,

    /**
     * Properties used for adjusting fields
     * for PDF Export and Page Breaks. Do not
     * remove.
     */
    internalTopPadding,
    pageBreakIntersectionPointsPixels,

  } = props;

  /**
   * Step 1: Theming and Field Styles
   */
  const theme = useContext(ThemeContext);
  const fieldTheme = getFieldThemeObject({
    mode: fieldMode,
    fieldPresentationMode,
    fieldPosition: props,
    fileStyles: fieldDefaultStyles,
    theme: theme?.field,
    sdkTheme: theme
  });

  const fieldProps = {
    id: _id,
    fieldId: field,
    fieldIdentifier: identifier,
    fieldPositionId: _id,
    isMobileView: view === FileViews.mobile,
    ...props,
    ...fieldTheme,
  };

  let fieldNode = null;
  let displayRequiredFieldIndicators = false;

  if (type === FieldTypes.file) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <FileField {...fieldProps} />
      </StyledField>
    );
  }

  if (type === FieldTypes.image) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));
    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <ImageField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.richText) {
    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <RTEField {...fieldProps} disabled={fieldMode !== FieldModes.edit} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.text
    || type === FieldTypes.number
    || type === FieldTypes.date
    || type === FieldTypes.uniqueId
  ) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField
        overflow={internalTopPadding}
        disabled={disabled}
      >
        <TextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.block) {
    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <BlockField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.textarea) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField overflow={internalTopPadding} disabled={disabled}>
        <TextareaField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.chart) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <ChartField
          {...fieldProps}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.signature) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <SignatureField {...fieldProps} disabled={fieldProps.disabled || fieldMode === FieldModes.edit} />
      </StyledField>
    );
  }

  /**
   * Dropdown Fields
   */
  else if (
    type === FieldTypes.dropdown
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.text)
  ) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value, options));

    fieldNode = (
      <StyledField
        overflow={internalTopPadding}
        disabled={disabled}
      >
        <DropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.dropdown
    && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.text)
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value, options));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <DropdownSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Multi-Select Fields
   */
  else if (type === FieldTypes.multiSelect
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.horizontal)) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value, options));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <MultiSelectField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.multiSelect && displayType !== FieldDisplayTypes.original) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value, options));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <MultiSelectSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
          value={fieldProps.value || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Table Fields
   */
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.original) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField>
        <TableField
          {...fieldProps}
          virtual={virtual}
          pageBreakPointsPixels={pageBreakIntersectionPointsPixels}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.text) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField
        overflow={internalTopPadding}
        disabled={disabled}
      >
        <TableTextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.image) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <TableImageField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.dropdown) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField
        overflow={internalTopPadding}
        disabled={disabled}
      >
        <TableDropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.table
    && columnType === FieldTableColumnTypes.dropdown
    && displayType !== FieldDisplayTypes.original
    && displayType !== FieldDisplayTypes.text
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.cellValue = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField overflow={internalTopPadding}>
        <TableDropdownSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.inputGroup) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(type, value));

    fieldNode = (
      <StyledField>
        <InputGroupField
          {...fieldProps}
          virtual={virtual}
          disabled={fieldProps.disabled || fieldMode === FieldModes.readonly}
          pageBreakPointsPixels={pageBreakIntersectionPointsPixels}
        />
      </StyledField>
    );
  }

  const indicatorNodes = (
    <StyledFieldIndicatorWrapper>
      {!displayRequiredFieldIndicators || fieldMode === FieldModes.readonly ? null : (
        <StyledFieldIndicator>
          <Icon icon={ThemeIcons.settings.required} color={theme.dangerColor} />
        </StyledFieldIndicator>
      )}
      {!hidden || fieldMode !== FieldModes.edit ? null : (
        <StyledFieldIndicator>
          <Icon icon={ThemeIcons.settings.hide} color={theme.warningColor} />
        </StyledFieldIndicator>
      )}
    </StyledFieldIndicatorWrapper>
  );

  const styledFieldWrapperProps = { disabled };

  if (lazyload) {
    return (
      <StyledFieldWrapper {...styledFieldWrapperProps}>
        <FieldLazyLoader>
          {indicatorNodes}
          {fieldNode}
        </FieldLazyLoader>
      </StyledFieldWrapper>
    );
  } else {
    return (
      <StyledFieldWrapper
        {...styledFieldWrapperProps}
      >
        {indicatorNodes}
        {fieldNode}
      </StyledFieldWrapper>
    );
  }

};

export default memo(Field);
